<template>
  <div class="repair-inventory main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Tồn kho sản phẩm sửa chữa'">
        <template v-slot:preview>
          <!-- Bộ lọc cửa hàng nằm ngoài tab và GIỮ NGUYÊN khi chuyển tab: thủ kho chọn cửa
               hàng một lần rồi xem tồn kho và thẻ kho mà không phải chọn lại. -->
          <b-row class="mb-3">
            <b-col md="4">
              <b-form-group class="mb-0" label="Cửa hàng" label-cols-sm="3">
                <b-form-select
                  v-model="storeId"
                  :options="storeOptions"
                  :disabled="storeLoading || !canPickStore"
                  size="sm"
                  @change="onStoreChange"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group class="mb-0" label="Tìm kiếm" label-cols-sm="3">
                <b-form-input
                  v-model="keyword"
                  size="sm"
                  :placeholder="activeTab === 0 ? 'Tên / mã sản phẩm' : 'IMEI'"
                  @keyup.enter="reload"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4" class="text-right">
              <b-button variant="primary" size="sm" @click="reload">
                <i class="fas fa-search mr-1"></i>Lọc
              </b-button>
              <b-button
                v-if="canViewAll"
                variant="outline-secondary"
                size="sm"
                class="ml-2"
                :disabled="integrity.running"
                title="Đối chiếu số tổng hợp với số máy thực tế trong kho"
                @click="runIntegrityCheck"
              >
                <i class="fas fa-balance-scale mr-1"></i>
                {{ integrity.running ? 'Đang kiểm...' : 'Đối soát' }}
              </b-button>
            </b-col>
          </b-row>

          <b-alert
            v-if="integrity.checked"
            :variant="integrity.hasIssue ? 'danger' : 'success'"
            show
            dismissible
            class="py-2"
            @dismissed="integrity.checked = false"
          >
            <span v-if="!integrity.hasIssue">
              Đối soát xong — số tổng hợp khớp với số máy thực tế trong kho.
            </span>
            <span v-else>
              Phát hiện sai lệch:
              <strong>{{ integrity.aggregateCount }}</strong> dòng lệch giữa số tổng hợp và
              chi tiết máy,
              <strong>{{ integrity.slipCount }}</strong> dòng lệch giữa phiếu sửa chữa và kho.
              Vui lòng báo bộ phận kỹ thuật.
            </span>
          </b-alert>

          <b-tabs v-model="activeTab" @input="onTabChange">
            <b-tab title="Tồn kho sửa chữa" />
            <b-tab title="Thẻ kho" />
          </b-tabs>

          <!-- ================= TAB 1: Tồn kho tổng hợp theo model ================= -->
          <b-table
            v-if="activeTab === 0"
            class="mt-3 table-vertical-center"
            :items="stockRows"
            :fields="stockFields"
            :busy="loading"
            show-empty
            empty-text="Chưa có máy nào trong kho sửa chữa"
            bordered
            small
            hover
            responsive
          >
            <template #table-busy>
              <div class="text-center text-primary my-3">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ml-2">Đang tải...</strong>
              </div>
            </template>

            <template #cell(product)="{ item }">
              <a
                href="javascript:void(0)"
                class="font-weight-bold"
                @click="openDetail(item)"
              >
                {{ item.productName || '—' }}
              </a>
              <div class="small text-muted">{{ item.productCode }}</div>
            </template>

            <template #cell(store)="{ item }">
              {{ item.storeName || ('CH #' + item.storeId) }}
            </template>

            <template #cell(totalQuantity)="{ item }">
              <span class="font-weight-bold">{{ item.totalQuantity }}</span>
            </template>

            <!-- Sáu cột nhóm dựng động theo STOCK_GROUP_ORDER: thêm/bớt nhóm chỉ sửa constants. -->
            <template
              v-for="group in groupOrder"
              v-slot:[`cell(${group})`]="{ item }"
            >
              <span :key="group" :class="item.groups[group] ? '' : 'text-muted'">
                {{ item.groups[group] || 0 }}
              </span>
            </template>

            <template #cell(maxAgingDays)="{ item }">
              <span
                :class="agingClass(item.agingLevel)"
                :title="agingTitle(item.agingLevel)"
              >
                {{ item.maxAgingDays }} ngày
              </span>
            </template>
          </b-table>

          <!-- ================= TAB 2: Thẻ kho ================= -->
          <b-table
            v-else
            class="mt-3 table-vertical-center"
            :items="historyRows"
            :fields="historyFields"
            :busy="loading"
            show-empty
            empty-text="Không có biến động nào trong khoảng thời gian này"
            bordered
            small
            responsive
          >
            <template #table-busy>
              <div class="text-center text-primary my-3">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ml-2">Đang tải...</strong>
              </div>
            </template>

            <template #cell(createdAt)="{ item }">
              {{ formatDateTime(item.createdAt) }}
            </template>

            <template #cell(code)="{ item }">
              <span class="font-weight-bold">{{ item.code }}</span>
              <div class="small text-muted">{{ item.typeName }}</div>
            </template>

            <template #cell(direction)="{ item }">
              <b-badge :variant="item.direction === 'in' ? 'success' : 'danger'">
                {{ item.direction === 'in' ? 'Nhập +1' : 'Xuất −1' }}
              </b-badge>
            </template>

            <template #cell(imeis)="{ item }">
              <div v-for="d in item.details" :key="d.imei || d.productId">
                {{ d.imei || '—' }}
              </div>
              <span v-if="!item.details.length" class="text-muted">—</span>
            </template>

            <template #cell(slip)="{ item }">
              <a
                v-if="item.repairSlipId"
                href="javascript:void(0)"
                @click="goToSlip(item.repairSlipId)"
              >
                Xem phiếu
              </a>
              <span v-else class="text-muted">—</span>
            </template>
          </b-table>

          <b-row class="mt-3 align-items-center">
            <b-col md="6">
              <p class="text-dark mb-0">Tổng: {{ totalRow }} dòng</p>
            </b-col>
            <b-col md="6">
              <b-pagination
                v-show="totalPage >= 2"
                v-model="page"
                :total-rows="totalRow"
                :per-page="limit"
                align="right"
                class="mb-0"
                @change="onPageChange"
              ></b-pagination>
            </b-col>
          </b-row>
        </template>
      </KTCodePreview>
    </div>

    <RepairInventoryDetailModal
      ref="detailModal"
      @open-slip="goToSlip"
      @open-history="goToHistoryOfImei"
    />
  </div>
</template>

<script>
/**
 * SRS t101478 — Tồn kho sản phẩm sửa chữa (FR-02, FR-03, FR-07).
 *
 * Màn hình CHỈ ĐỌC. Tồn kho phát sinh từ vòng đời Phiếu sửa chữa (nhận máy khi rời "Tiếp
 * nhận", trả máy khi đóng phiếu), không có thao tác nhập/xuất kho thủ công ở đây — giữ đúng
 * nguyên tắc phiếu sửa chữa là nguồn phát sinh duy nhất.
 *
 * Sáu nhóm tình trạng KHÔNG phải cột trong DB. BE suy ra từ RepairSlips.status và trả kèm
 * `groups` + `groupNames`; FE chỉ cố định thứ tự hiển thị và màu sắc.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { makeToastFaile } from '@/utils/common';
import { checkPermissions } from '@/utils/saveDataToLocal';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { extractError, formatDateTime } from '@/utils/warranty-service.helpers';
import {
  STOCK_GROUP_META,
  STOCK_GROUP_ORDER,
  AGING_META,
} from '@/utils/warranty-service.constants';
import RepairInventoryDetailModal from '@/view/components/warranty-service/RepairInventoryDetailModal.vue';

/** Trùng tên với VIEW_ALL_PERMISSION ở repair-inventory.service.js (BE) — đổi thì đổi cả hai. */
const VIEW_ALL_PERMISSION = 'REPAIR_STOCK_VIEW_ALL';

const BASE_API = 'v2/warranty-service/repair-inventory';

const STOCK_FIELDS = Object.freeze([
  { key: 'store', label: 'Cửa hàng' },
  { key: 'product', label: 'Model máy' },
  { key: 'totalQuantity', label: 'Đang giữ', tdClass: 'text-center' },
  ...STOCK_GROUP_ORDER.map((group) => ({
    key: group,
    label: STOCK_GROUP_META[group].text,
    tdClass: 'text-center',
  })),
  { key: 'maxAgingDays', label: 'Tồn lâu nhất', tdClass: 'text-center' },
]);

const HISTORY_FIELDS = Object.freeze([
  { key: 'createdAt', label: 'Thời điểm' },
  { key: 'code', label: 'Chứng từ' },
  { key: 'direction', label: 'Biến động', tdClass: 'text-center' },
  { key: 'imeis', label: 'IMEI' },
  { key: 'slip', label: 'Phiếu sửa chữa', tdClass: 'text-center' },
]);

export default {
  name: 'RepairInventory',
  components: { KTCodePreview, RepairInventoryDetailModal },
  data() {
    return {
      activeTab: 0,
      storeId: null,
      storeOptions: [{ value: null, text: 'Tất cả cửa hàng' }],
      storeLoading: false,
      canPickStore: false,
      canViewAll: false,
      keyword: '',
      stockRows: [],
      historyRows: [],
      loading: false,
      page: 1,
      limit: 20,
      totalRow: 0,
      totalPage: 0,
      integrity: {
        running: false,
        checked: false,
        hasIssue: false,
        aggregateCount: 0,
        slipCount: 0,
      },
    };
  },
  computed: {
    stockFields: () => STOCK_FIELDS,
    historyFields: () => HISTORY_FIELDS,
    groupOrder: () => STOCK_GROUP_ORDER,
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Dịch vụ bảo hành' },
      { title: 'Tồn kho sản phẩm sửa chữa' },
    ]);
    await this.fetchStores();
    this.fetchData();
  },
  methods: {
    formatDateTime,
    agingClass(level) {
      return (AGING_META[level] && AGING_META[level].variant) || '';
    },
    agingTitle(level) {
      return (AGING_META[level] && AGING_META[level].title) || '';
    },
    async fetchStores() {
      this.storeLoading = true;
      try {
        const { data } = await ApiService.query('stores/get-by-user');
        const payload = (data && data.data) || {};
        const stores = payload.stores || [];
        // Lấy quyền từ listFeature, KHÔNG dùng payload.viewAll của stores/get-by-user:
        // BE set cờ đó theo CHỨC DANH (StoreController.getUserStore — QL/TL/KTBH/TVBH thì 1
        // cửa hàng, chức danh khác thì xem tất cả), không theo REPAIR_STOCK_VIEW_ALL. Hai thứ
        // lệch nhau thì màn hình hiện nút "Đối soát" cho người không có quyền (bấm vào ăn 403),
        // hoặc giấu mất tính năng xem-tất-cả của người có quyền.
        this.canViewAll = checkPermissions([VIEW_ALL_PERMISSION]);
        // Quyền xem cửa hàng mình = đúng MỘT cửa hàng (repair-inventory.service.js
        // resolveStoreScope đọc storeId trong JWT), nên chỉ người xem-tất-cả mới được đổi.
        // Cho chọn theo stores.length sẽ mời user bấm vào cửa hàng mà BE chắc chắn chặn.
        this.canPickStore = this.canViewAll;
        this.storeOptions = [
          { value: null, text: 'Tất cả cửa hàng' },
          ...stores.map((s) => ({ value: s.id, text: s.name })),
        ];
        if (!this.canViewAll && payload.storeId) {
          this.storeId = payload.storeId;
        }
      } catch (err) {
        makeToastFaile('Không tải được danh sách cửa hàng');
      } finally {
        this.storeLoading = false;
      }
    },
    onStoreChange() {
      this.page = 1;
      this.$nextTick(() => this.fetchData());
    },
    onTabChange() {
      // Giữ nguyên bộ lọc cửa hàng, chỉ reset phân trang và kết quả cũ.
      this.page = 1;
      this.stockRows = [];
      this.historyRows = [];
      this.totalRow = 0;
      this.fetchData();
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.$nextTick(() => this.fetchData());
    },
    reload() {
      this.page = 1;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        if (this.activeTab === 0) {
          await this.fetchStocks();
        } else {
          await this.fetchHistories();
        }
      } catch (err) {
        makeToastFaile(extractError(err));
        this.stockRows = [];
        this.historyRows = [];
        this.totalRow = 0;
      } finally {
        this.loading = false;
      }
    },
    async fetchStocks() {
      const { data } = await ApiService.query(`${BASE_API}/stocks`, {
        params: {
          storeId: this.storeId || undefined,
          keyword: this.keyword || undefined,
          page: this.page,
          limit: this.limit,
        },
      });
      const payload = (data && data.data) || {};
      this.stockRows = payload.items || [];
      this.totalRow = payload.total || 0;
      this.totalPage = Math.ceil(this.totalRow / this.limit);
    },
    async fetchHistories() {
      const { data } = await ApiService.query(`${BASE_API}/histories`, {
        params: {
          storeId: this.storeId || undefined,
          // Ở tab thẻ kho, ô tìm kiếm dùng để lọc theo IMEI.
          imei: this.keyword || undefined,
          page: this.page,
          limit: this.limit,
        },
      });
      const payload = (data && data.data) || {};
      this.historyRows = payload.items || [];
      this.totalRow = payload.total || 0;
      this.totalPage = Math.ceil(this.totalRow / this.limit);
    },
    openDetail(row) {
      this.$refs.detailModal.open(row);
    },
    goToSlip(id) {
      const route = this.$router.resolve({ name: 'edit-repair-slip', params: { id } });
      window.open(route.href, '_blank');
    },
    /** Từ chi tiết một máy nhảy sang tab thẻ kho đã lọc sẵn IMEI đó. */
    goToHistoryOfImei(imei) {
      this.$bvModal.hide('modal-repair-inventory-detail');
      this.keyword = imei || '';
      this.page = 1;
      // Đổi activeTab làm b-tabs bắn `input` → onTabChange() đã gọi fetchData(). Gọi thêm
      // một lần ở đây nữa là bắn hai request /histories giống hệt nhau. Chỉ tự gọi khi đang
      // sẵn ở tab thẻ kho, vì lúc đó giá trị không đổi nên không có sự kiện nào bắn ra.
      if (this.activeTab === 1) {
        this.fetchData();
      } else {
        this.activeTab = 1;
      }
    },
    async runIntegrityCheck() {
      this.integrity.running = true;
      try {
        const { data } = await ApiService.query(`${BASE_API}/integrity-check`, {
          params: { storeId: this.storeId || undefined },
        });
        const payload = (data && data.data) || {};
        this.integrity.hasIssue = !!payload.hasIssue;
        this.integrity.aggregateCount = (payload.aggregateMismatches || []).length;
        this.integrity.slipCount = (payload.slipMismatches || []).length;
        this.integrity.checked = true;
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.integrity.running = false;
      }
    },
  },
};
</script>
